// HvacUnits — Equipment Radar for HVAC.
// Combines Seasonal Maintenance Radar (a) + AI Diagnostic Advisor (b) +
// Photo-to-Fault ID (c) + Equipment Journal + Branded PDF (d).
import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Snowflake, Plus, Trash, CheckCircle, X, Warning, Clock, Calendar,
  Camera, Sparkle, Path, FilePdf, MapPinLine, Wrench, Calculator, TestTube,
} from "@phosphor-icons/react";
import LoadCalcPopover from "@/pages/LoadCalcPopover";
import CatalogAutocomplete from "@/components/CatalogAutocomplete";
import { matchHvacUnits, HVAC_COMMON_FIXES } from "@/data/hvacUnits";

const STATUS_META = {
  overdue:   { color: "bg-red-500 text-white",       label: "OVERDUE"   },
  this_week: { color: "bg-amber-400 text-slate-900", label: "THIS WEEK" },
  soon:      { color: "bg-cyan-500 text-white",      label: "SOON"      },
  future:    { color: "bg-slate-400 text-white",     label: "FUTURE"    },
  unset:     { color: "bg-slate-300 text-slate-700", label: "UNSET"     },
};
const SYSTEM_LABELS = {
  central_ac: "Central AC", heat_pump: "Heat Pump",
  gas_furnace: "Gas Furnace", electric_furnace: "Electric Furnace",
  boiler: "Boiler", mini_split: "Mini-Split",
  package_unit: "Package Unit", rooftop_unit: "Rooftop Unit", other: "Other",
};

const initialUnit = {
  equipment_id: "", nickname: "", system_type: "central_ac",
  site_address: "", install_date: "", warranty_years: "",
  pm_cadence: "biannual", next_pm_date: "",
  contact_name: "", contact_phone: "", notes: "",
};

export default function HvacUnits() {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialUnit);
  const [saving, setSaving] = useState(false);
  // Diagnostic Advisor
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagForm, setDiagForm] = useState({ system_type: "central_ac", symptoms: "", outdoor_temp_f: "", equipment_age_years: "", notes: "" });
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  // Photo-to-Fault ID
  const [idLoading, setIdLoading] = useState(false);
  const [idResult, setIdResult] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [linkUnitId, setLinkUnitId] = useState("");
  // Journal
  const [journalFor, setJournalFor] = useState(null);
  const [journalData, setJournalData] = useState(null);
  // AI Specs & Field Tests
  const [specsFor, setSpecsFor] = useState(null);        // the unit row being inspected
  const [specsData, setSpecsData] = useState(null);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [specsFromCache, setSpecsFromCache] = useState(false);
  // Load Calc popover
  const [loadCalcOpen, setLoadCalcOpen] = useState(false);
  // Auto-Link
  const [activeUnit, setActiveUnit] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hvac_active_unit") || "null"); }
    catch { return null; }
  });
  useEffect(() => {
    if (activeUnit?.id) setLinkUnitId(activeUnit.id);
  }, [activeUnit]);

  const load = async () => {
    const r = await api.get("/hvac/units");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.equipment_id || !form.site_address) {
      toast.error("Equipment ID and site address are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hvac/units", {
        ...form,
        warranty_years: form.warranty_years ? parseInt(form.warranty_years, 10) : null,
        install_date: form.install_date || null,
        next_pm_date: form.next_pm_date || null,
      });
      toast.success("Unit saved");
      setForm(initialUnit);
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this unit from the radar?")) return;
    await api.delete(`/hvac/units/${id}`);
    await load();
  };

  const markServiced = async (id) => {
    try {
      await api.post(`/hvac/units/${id}/mark-serviced`);
      toast.success("PM logged — next visit auto-scheduled");
      await load();
    } catch { toast.error("Update failed"); }
  };

  // Diagnostic Advisor
  const runDiagnose = async () => {
    if (!diagForm.symptoms || diagForm.symptoms.length < 3) {
      toast.error("Describe the symptoms first");
      return;
    }
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const { data } = await api.post("/hvac/ai-diagnose", {
        ...diagForm,
        outdoor_temp_f: diagForm.outdoor_temp_f ? parseFloat(diagForm.outdoor_temp_f) : null,
        equipment_age_years: diagForm.equipment_age_years ? parseInt(diagForm.equipment_age_years, 10) : null,
      });
      setDiagResult(data.diagnosis);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI failed — try again"));
    } finally { setDiagLoading(false); }
  };

  // Photo-to-Fault ID
  const identifyPhoto = async (file) => {
    if (!file) return;
    setIdLoading(true);
    setIdResult(null);
    setIdPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = linkUnitId ? `/hvac/ai-photo-id?unit_id=${encodeURIComponent(linkUnitId)}` : "/hvac/ai-photo-id";
      const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdResult(r.data.identification);
      if (r.data.journal_saved) toast.success("Saved to unit journal");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "ID failed — try a clearer photo"));
    } finally { setIdLoading(false); }
  };

  // AI Specs & Field Tests — decode nameplate + give test targets
  const openSpecs = async (u) => {
    setSpecsFor(u); setSpecsData(null); setSpecsLoading(true); setSpecsFromCache(false);
    try {
      const { data } = await api.post("/hvac/ai-unit-specs", {
        equipment_id: u.equipment_id, system_type: u.system_type,
      });
      setSpecsData(data.specs); setSpecsFromCache(!!data.from_cache);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI specs failed — try again"));
      setSpecsFor(null);
    } finally { setSpecsLoading(false); }
  };

  // Journal + PDF
  const openJournal = async (u) => {
    setJournalFor(u); setJournalData(null);
    try {
      const r = await api.get(`/hvac/units/${u.id}/journal`);
      setJournalData(r.data);
    } catch { toast.error("Could not load journal"); }
  };
  const downloadPDF = (unitId) => {
    const token = localStorage.getItem("access_token");
    window.open(`${API}/hvac/units/${unitId}/journal.pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
  };
  const photoUrl = (path) => {
    const token = localStorage.getItem("access_token");
    return `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;
  };

  // Auto-Link (Start Job)
  const startJob = (u) => {
    const payload = { id: u.id, name: u.nickname || u.equipment_id, address: u.site_address };
    localStorage.setItem("hvac_active_unit", JSON.stringify(payload));
    setActiveUnit(payload); setLinkUnitId(u.id);
    toast.success(`On-site at ${payload.name} — photos auto-link`);
  };
  const endJob = () => {
    localStorage.removeItem("hvac_active_unit");
    setActiveUnit(null); setLinkUnitId("");
    toast.info("Job ended");
  };

  if (!data) return <div className="p-6 text-slate-500">Loading units…</div>;

  return (
    <AdminShell>
    <div className="p-4 md:p-6" data-testid="hvac-units-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0f172a] text-white flex items-center justify-center">
            <Snowflake weight="fill" size={22} className="text-cyan-400"/>
          </div>
          <div>
            <h1 className="font-display font-black text-3xl leading-none">Equipment Radar</h1>
            <div className="text-xs uppercase font-black tracking-widest text-slate-500 mt-1">
              Seasonal PMs · AI diagnose · Photo ID · Branded PDF
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg cursor-pointer">
            <Camera weight="fill" size={16}/> {idLoading ? "Identifying…" : "ID Photo"}
            <input type="file" accept="image/*" className="hidden"
              data-testid="hvac-photo-id-input"
              onChange={e => { const f = e.target.files?.[0]; if (f) identifyPhoto(f); e.target.value = ""; }}/>
          </label>
          <button onClick={() => setLoadCalcOpen(true)} data-testid="hvac-loadcalc-btn"
            className="h-11 px-4 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Calculator weight="fill" size={16}/> Load Calc
          </button>
          <button onClick={() => { setDiagOpen(true); setDiagResult(null); }}
            data-testid="hvac-diagnose-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Sparkle weight="fill" size={16}/> AI Diagnose
          </button>
          <button onClick={() => setShowForm(v => !v)} data-testid="hvac-add-btn"
            className="h-11 px-5 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 shadow-lg">
            <Plus weight="bold" size={16}/> {showForm ? "Cancel" : "New Unit"}
          </button>
        </div>
      </div>

      {activeUnit && (
        <div className="mb-4 p-3 rounded-xl bg-fuchsia-600 text-white flex items-center gap-3 shadow-lg" data-testid="hvac-active-banner">
          <MapPinLine weight="fill" size={20}/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Currently On-Site · Photos auto-link</div>
            <div className="font-bold text-sm truncate">{activeUnit.name} · {activeUnit.address}</div>
          </div>
          <button onClick={endJob} data-testid="hvac-active-end"
            className="h-8 px-3 bg-white/20 hover:bg-white/30 text-white font-black uppercase tracking-widest text-[10px] rounded-lg">
            End Job
          </button>
        </div>
      )}

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="hvac-counts">
        {["overdue","this_week","soon","future","unset"].map(k => {
          const m = STATUS_META[k];
          return (
            <div key={k} className={`p-3 rounded-xl ${m.color}`}>
              <div className="text-2xl font-display font-black">{data.counts?.[k] ?? 0}</div>
              <div className="text-[10px] font-black uppercase tracking-widest">{m.label}</div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="bg-white border-2 border-[#00C4CC] rounded-2xl p-4 md:p-5 mb-5 shadow-lg" data-testid="hvac-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Equipment ID / Model # *">
              <CatalogAutocomplete
                value={form.equipment_id}
                onChange={v => setF("equipment_id", v)}
                onSelect={u => {
                  setForm(prev => ({
                    ...prev,
                    equipment_id: u.model,
                    nickname: prev.nickname || `${u.brand} ${u.tons ? u.tons+"-ton" : ""}`.trim(),
                    system_type: u.kind || prev.system_type,
                  }));
                  toast.success(`${u.brand} ${u.model} · ${u.tons ? u.tons+"-ton " : ""}${u.seer ? "SEER "+u.seer : ""}`);
                }}
                matchFn={matchHvacUnits}
                keyFn={(u) => `${u.brand}-${u.model}`}
                testid="hvac-equip-id"
                placeholder="Start typing… 25HCB6, Carrier, MSZ-FS12NA"
                disclaimer="Curated model list — free-typing is fine. Confirm serial from nameplate."
                renderOption={(u) => (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-900">{u.model}</span>
                      <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{u.brand}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {u.notes}
                      {u.tons && <> · <span className="font-mono">{u.tons}-ton</span></>}
                      {u.seer && <> · <span className="font-mono">SEER {u.seer}</span></>}
                    </div>
                    <div className="mt-1 flex items-center gap-1 flex-wrap">
                      <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">{u.kind.replace("_", " ")}</span>
                      {u.stage && <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{u.stage}</span>}
                      {u.refrigerant && <span className="text-[9px] font-black uppercase tracking-widest bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{u.refrigerant}</span>}
                    </div>
                  </>
                )}
              />
            </Field>
            <Field label="Nickname"><input value={form.nickname} onChange={e => setF("nickname", e.target.value)} placeholder="Roof unit A" data-testid="hvac-nickname" className={inputCls}/></Field>
            <Field label="System Type"><select value={form.system_type} onChange={e => setF("system_type", e.target.value)} data-testid="hvac-system-type" className={inputCls}>{(data.system_types || []).map(s => <option key={s} value={s}>{SYSTEM_LABELS[s] || s}</option>)}</select></Field>
            <Field label="Site Address *"><input value={form.site_address} onChange={e => setF("site_address", e.target.value)} placeholder="1200 Sample St" data-testid="hvac-address" className={inputCls}/></Field>
            <Field label="Install Date"><input type="date" value={form.install_date} onChange={e => setF("install_date", e.target.value)} data-testid="hvac-install" className={inputCls}/></Field>
            <Field label="Warranty (years)"><input type="number" min="0" max="25" value={form.warranty_years} onChange={e => setF("warranty_years", e.target.value)} placeholder="10" data-testid="hvac-warranty" className={inputCls}/></Field>
            <Field label="PM Cadence"><select value={form.pm_cadence} onChange={e => setF("pm_cadence", e.target.value)} data-testid="hvac-cadence" className={inputCls}>{(data.cadences || []).map(c => <option key={c} value={c}>{c[0].toUpperCase()+c.slice(1)}</option>)}</select></Field>
            <Field label="Next PM (blank = auto)"><input type="date" value={form.next_pm_date} onChange={e => setF("next_pm_date", e.target.value)} data-testid="hvac-next-pm" className={inputCls}/></Field>
            <Field label="Contact Name"><input value={form.contact_name} onChange={e => setF("contact_name", e.target.value)} placeholder="Owner name" data-testid="hvac-contact" className={inputCls}/></Field>
            <Field label="Contact Phone"><input value={form.contact_phone} onChange={e => setF("contact_phone", e.target.value)} placeholder="215-555-0100" data-testid="hvac-phone" className={inputCls}/></Field>
          </div>
          <button onClick={save} disabled={saving} data-testid="hvac-save-btn" className="mt-4 h-11 px-6 bg-[#00C4CC] hover:bg-[#00a9b1] text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center gap-2 disabled:opacity-50 shadow">
            {saving ? "Saving…" : "Save Unit"}
          </button>
        </div>
      )}

      {/* Units list */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {data.units.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="hvac-empty">
            No units yet. Tap <b>New Unit</b> to add your first piece of equipment.
          </div>
        ) : data.units.map(u => {
          const m = STATUS_META[u.pm_status] || STATUS_META.unset;
          const w = u.warranty || {};
          return (
            <div key={u.id} data-testid={`hvac-row-${u.id}`}
              className="p-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center gap-3">
              <div className={`h-9 min-w-[90px] px-2 rounded-lg inline-flex items-center justify-center gap-1 ${m.color}`}>
                <span className="font-black text-[10px] uppercase tracking-widest">{m.label}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{u.nickname || u.equipment_id}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{SYSTEM_LABELS[u.system_type] || u.system_type}</span>
                  {w.has_warranty && (
                    <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      w.state === "expired" ? "bg-red-100 text-red-700" :
                      w.state === "expiring_soon" ? "bg-amber-100 text-amber-700" :
                      "bg-emerald-100 text-emerald-700"}`}>
                      {w.state === "expired" ? "Warranty Expired" : w.state === "expiring_soon" ? `Warranty ${w.days_left}d` : "Under Warranty"}
                    </span>
                  )}
                  {u.journal_count > 0 && (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5 rounded">
                      {u.journal_count} photo{u.journal_count===1?"":"s"}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 truncate">{u.site_address}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {u.pm_cadence?.toUpperCase()} · Next: <span className="font-mono">{u.next_pm_date || "—"}</span>
                  {typeof u.days_until === "number" && (
                    <span className={`ml-2 ${u.days_until < 0 ? "text-red-600 font-black" : ""}`}>
                      {u.days_until < 0 ? `${Math.abs(u.days_until)}d overdue` : `in ${u.days_until}d`}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                {activeUnit?.id === u.id ? (
                  <button onClick={endJob} data-testid={`hvac-endjob-${u.id}`} className="h-9 px-3 bg-emerald-500 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                    <MapPinLine weight="fill" size={12}/> On-Site
                  </button>
                ) : (
                  <button onClick={() => startJob(u)} data-testid={`hvac-startjob-${u.id}`} className="h-9 px-3 bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-800 font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1">
                    <MapPinLine weight="fill" size={12}/> Start Job
                  </button>
                )}
                <button onClick={() => openSpecs(u)} data-testid={`hvac-specs-${u.id}`} className="h-9 px-3 bg-cyan-600 hover:bg-cyan-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <TestTube weight="fill" size={12}/> AI Specs
                </button>
                <button onClick={() => openJournal(u)} data-testid={`hvac-journal-${u.id}`} className="h-9 px-3 bg-slate-800 hover:bg-slate-900 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <Camera weight="fill" size={12}/> Journal
                </button>
                <button onClick={() => markServiced(u.id)} data-testid={`hvac-serviced-${u.id}`} className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                  <CheckCircle weight="fill" size={12}/> PM Done
                </button>
                <button onClick={() => remove(u.id)} data-testid={`hvac-remove-${u.id}`} className="h-9 w-9 rounded-lg bg-slate-100 hover:bg-red-100 hover:text-red-600 text-slate-400 inline-flex items-center justify-center">
                  <Trash size={14}/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Diagnose Modal */}
      {diagOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => !diagLoading && setDiagOpen(false)} data-testid="hvac-diag-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Sparkle weight="fill" size={22} className="text-fuchsia-500"/>
                <div className="font-display font-black text-xl">AI Diagnostic Advisor</div>
              </div>
              <button onClick={() => setDiagOpen(false)} className="text-slate-400 hover:text-slate-800" data-testid="hvac-diag-close"><X size={20}/></button>
            </div>
            <div className="p-5">
              {!diagResult ? (
                <>
                  <div className="text-xs text-slate-500 mb-3">Describe what the system is doing (or NOT doing). AI will suggest likely causes and diagnostic order.</div>
                  <div className="space-y-3">
                    <Field label="System Type">
                      <select value={diagForm.system_type} onChange={e => setDiagForm(p => ({ ...p, system_type: e.target.value }))} data-testid="hvac-diag-system" className={inputCls}>
                        {(data.system_types || []).map(s => <option key={s} value={s}>{SYSTEM_LABELS[s] || s}</option>)}
                      </select>
                    </Field>
                    <Field label="Symptoms *">
                      <textarea rows={3} value={diagForm.symptoms} onChange={e => setDiagForm(p => ({ ...p, symptoms: e.target.value }))}
                        placeholder="Blowing warm air, condenser fan not spinning, capacitor slightly bulged, humming when calling for cool"
                        data-testid="hvac-diag-symptoms"
                        className="w-full px-3 py-2 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium"/>
                    </Field>
                    {/* Common Fixes — deterministic checklist, ZERO LLM cost */}
                    <div data-testid="hvac-common-fixes">
                      <div className="text-[10px] uppercase font-black tracking-widest text-emerald-700 mb-1.5 inline-flex items-center gap-1">
                        <Wrench weight="fill" size={11}/> Common Fixes — instant, no AI call
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {HVAC_COMMON_FIXES.map(f => (
                          <button key={f.id} type="button"
                            data-testid={`hvac-quickfix-${f.id}`}
                            onClick={() => {
                              // Feed the checklist into the result panel as a pre-canned answer.
                              setDiagResult({
                                from_common_fixes: true,
                                confidence: "high",
                                likely_causes: f.likely.map((cause, i) => ({
                                  cause, probability: i === 0 ? "high" : i === 1 ? "medium" : "low",
                                  why: "Known failure mode for this symptom pattern",
                                  part: (f.parts_typical || "").split(" · ")[i] || "other",
                                })),
                                diagnostic_order: f.diagnostic_steps,
                                safety: ["De-energize + LOTO before touching contactors / capacitors"],
                                escalate_if: "If the checklist doesn't isolate the fault, run full AI Diagnose",
                              });
                            }}
                            className="text-left px-2.5 py-2 border-2 border-emerald-200 hover:border-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-lg text-[11px] font-bold text-emerald-900 leading-tight">
                            {f.label}
                          </button>
                        ))}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1.5 italic">
                        None of these? Type symptoms below → AI Diagnose costs a fraction of a cent (cached 7d).
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Equipment Age (yrs)"><input type="number" value={diagForm.equipment_age_years} onChange={e => setDiagForm(p => ({ ...p, equipment_age_years: e.target.value }))} placeholder="12" data-testid="hvac-diag-age" className={inputCls}/></Field>
                      <Field label="Outdoor Temp (°F)"><input type="number" value={diagForm.outdoor_temp_f} onChange={e => setDiagForm(p => ({ ...p, outdoor_temp_f: e.target.value }))} placeholder="92" data-testid="hvac-diag-temp" className={inputCls}/></Field>
                    </div>
                  </div>
                  <button onClick={runDiagnose} disabled={diagLoading} data-testid="hvac-diag-submit" className="mt-4 w-full h-12 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow">
                    <Sparkle weight="fill" size={14}/> {diagLoading ? "Thinking…" : "Diagnose"}
                  </button>
                </>
              ) : (
                <div data-testid="hvac-diag-result">
                  <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-600 mb-1">
                    AI Diagnosis · Confidence: {diagResult.confidence || "medium"}
                  </div>
                  {(diagResult.likely_causes || []).length > 0 && (
                    <div className="mt-2 mb-4">
                      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Likely Causes</div>
                      {diagResult.likely_causes.map((c, i) => (
                        <div key={i} className="border-l-4 border-fuchsia-500 pl-3 py-1 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">{c.cause}</span>
                            <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                              c.probability==="high"?"bg-red-100 text-red-700":c.probability==="low"?"bg-slate-100 text-slate-700":"bg-amber-100 text-amber-700"}`}>
                              {c.probability}
                            </span>
                            {c.part && <span className="text-[10px] font-mono text-slate-500">→ {c.part}</span>}
                            {c.part && c.part !== "other" && (
                              <button type="button"
                                data-testid={`hvac-reorder-add-${i}`}
                                onClick={async () => {
                                  try {
                                    await api.post("/parts-reorder", {
                                      part: c.part, qty: 1, source: "ai_diagnose",
                                      unit_ref: diagForm?.equipment_id || undefined,
                                      job_ref: activeUnit ? `${activeUnit.name} — ${activeUnit.address || ""}`.trim() : undefined,
                                      notes: c.cause,
                                    });
                                    toast.success(activeUnit ? `Added ${c.part} → ${activeUnit.name}` : `Added ${c.part} to truck reorder list`);
                                  } catch (e) { toast.error("Could not add to reorder"); }
                                }}
                                className="ml-auto h-6 px-2 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                                + Truck
                              </button>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5">{c.why}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(diagResult.diagnostic_order || []).length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] uppercase font-black tracking-widest text-cyan-700 mb-1">Diagnostic Order</div>
                      <ol className="list-decimal pl-5 text-sm space-y-1">
                        {diagResult.diagnostic_order.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    </div>
                  )}
                  {(diagResult.safety || []).length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] uppercase font-black tracking-widest text-amber-700 mb-1">Safety</div>
                      <ul className="list-disc pl-5 text-sm space-y-1 text-amber-800">
                        {diagResult.safety.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {diagResult.escalate_if && (
                    <div className="p-3 rounded-lg bg-red-50 border-l-4 border-red-500 text-red-900 text-sm">
                      <b>Escalate if:</b> {diagResult.escalate_if}
                    </div>
                  )}
                  <button onClick={() => setDiagResult(null)} data-testid="hvac-diag-reset" className="mt-4 w-full h-10 border-2 border-slate-300 hover:bg-slate-100 text-slate-700 font-black uppercase tracking-widest text-xs rounded-xl">
                    Ask Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo-to-Fault ID Modal */}
      {(idResult || idLoading) && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => !idLoading && (setIdResult(null), setIdPreview(null))} data-testid="hvac-id-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Camera weight="fill" size={22} className="text-emerald-500"/>
                <div className="font-display font-black text-xl">Photo-to-Fault ID</div>
              </div>
              {!idLoading && <button onClick={() => { setIdResult(null); setIdPreview(null); }} className="text-slate-400 hover:text-slate-800" data-testid="hvac-id-close"><X size={20}/></button>}
            </div>
            <div className="p-5">
              {idPreview && <img src={idPreview} alt="fault" className="w-full max-h-56 object-contain rounded-lg mb-4 bg-slate-50"/>}
              {idLoading ? (
                <div className="text-center py-6">
                  <div className="text-sm font-bold text-slate-600 mb-2">Analyzing photo…</div>
                  <div className="text-[11px] text-slate-500">Claude Vision is looking at the image</div>
                </div>
              ) : idResult && (
                <div data-testid="hvac-id-result">
                  <div className="text-[10px] uppercase font-black tracking-widest text-emerald-600 mb-1">
                    Identified · Confidence: {idResult.confidence}
                  </div>
                  <div className="font-display font-black text-2xl leading-tight" data-testid="hvac-id-name">
                    {idResult.part_specific || (idResult.part || "unknown").replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase())}
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {idResult.condition && (
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                        idResult.condition==="failed"?"bg-red-100 text-red-800":idResult.condition==="worn"?"bg-amber-100 text-amber-800":idResult.condition==="good"?"bg-emerald-100 text-emerald-800":"bg-slate-100 text-slate-700"}`}>
                        {idResult.condition}
                      </span>
                    )}
                    {idResult.urgency && (
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                        idResult.urgency==="urgent"?"bg-red-100 text-red-800":idResult.urgency==="low"?"bg-slate-100 text-slate-700":"bg-cyan-100 text-cyan-800"}`}>
                        {idResult.urgency}
                      </span>
                    )}
                  </div>
                  {(idResult.brand || idResult.model_number || idResult.serial_number) && (
                    <div className="mt-4 p-3 rounded-lg bg-cyan-50 border-l-4 border-cyan-500" data-testid="hvac-id-dataplate">
                      <div className="text-[10px] uppercase font-black tracking-widest text-cyan-800 mb-1">Data Plate Read</div>
                      {idResult.brand && <div className="text-xs"><b>Brand:</b> {idResult.brand}</div>}
                      {idResult.model_number && <div className="text-xs font-mono"><b>Model:</b> {idResult.model_number}</div>}
                      {idResult.serial_number && <div className="text-xs font-mono"><b>Serial:</b> {idResult.serial_number}</div>}
                    </div>
                  )}
                  {(idResult.observed || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">What AI Saw</div>
                      <ul className="list-disc pl-5 text-sm space-y-1">
                        {idResult.observed.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {idResult.next_step && (
                    <div className="mt-4 p-3 rounded-lg bg-fuchsia-50 border-l-4 border-fuchsia-500 text-fuchsia-900 text-sm">
                      <b>Next step:</b> {idResult.next_step}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Journal Modal */}
      {journalFor && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => { setJournalFor(null); setJournalData(null); }} data-testid="hvac-journal-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <Wrench weight="fill" size={22} className="text-cyan-500"/>
                <div>
                  <div className="font-display font-black text-xl">{journalFor.nickname || journalFor.equipment_id}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{journalFor.site_address}</div>
                </div>
              </div>
              <button onClick={() => { setJournalFor(null); setJournalData(null); }} className="text-slate-400 hover:text-slate-800" data-testid="hvac-journal-close"><X size={20}/></button>
            </div>
            <div className="p-5">
              {!journalData ? (
                <div className="text-center text-slate-400 py-6">Loading journal…</div>
              ) : (
                <>
                  <button onClick={() => downloadPDF(journalFor.id)} data-testid="hvac-journal-pdf-btn" className="w-full h-10 mb-4 bg-red-600 hover:bg-red-700 text-white font-black uppercase tracking-widest text-xs rounded-xl inline-flex items-center justify-center gap-2 shadow">
                    <FilePdf weight="fill" size={14}/> Download Branded PDF Report
                  </button>
                  {journalData.entries.length === 0 ? (
                    <div className="text-center text-slate-400 py-6" data-testid="hvac-journal-empty">
                      No photos yet. Tap <b>Start Job</b> on this unit, then use <b>ID Photo</b>.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {journalData.entries.map((e, i) => {
                        const id = e.identification || {};
                        return (
                          <div key={i} data-testid={`hvac-journal-entry-${i}`} className="flex gap-3 border border-slate-200 rounded-xl p-3">
                            {e.photo_path && (
                              <img src={photoUrl(e.photo_path)} alt="part" className="w-24 h-24 object-cover rounded-lg bg-slate-100 flex-shrink-0"/>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm">{id.part_specific || (id.part || "unknown").replace(/_/g," ")}</span>
                                {id.condition && (
                                  <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                    id.condition==="failed"?"bg-red-100 text-red-700":id.condition==="worn"?"bg-amber-100 text-amber-700":"bg-emerald-100 text-emerald-700"}`}>
                                    {id.condition}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                                {new Date(e.captured_at).toLocaleString()} · {e.captured_by || ""}
                              </div>
                              {(id.model_number || id.serial_number) && (
                                <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                                  {id.brand ? id.brand+" · " : ""}{id.model_number ? "Model "+id.model_number : ""}{id.serial_number ? " · S/N "+id.serial_number : ""}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* AI Specs & Field Tests Modal */}
      {specsFor && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => !specsLoading && setSpecsFor(null)} data-testid="hvac-specs-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2 min-w-0">
                <TestTube weight="fill" size={22} className="text-cyan-500 flex-shrink-0"/>
                <div className="min-w-0">
                  <div className="font-display font-black text-xl truncate">AI Specs & Field Tests</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">
                    {specsFor.nickname || specsFor.equipment_id} · Model {specsFor.equipment_id}
                  </div>
                </div>
              </div>
              <button onClick={() => setSpecsFor(null)} className="text-slate-400 hover:text-slate-800 flex-shrink-0" data-testid="hvac-specs-close"><X size={20}/></button>
            </div>
            <div className="p-5">
              {specsLoading ? (
                <div className="text-center py-8" data-testid="hvac-specs-loading">
                  <div className="text-sm font-bold text-slate-600 mb-2">Decoding nameplate + pulling test targets…</div>
                  <div className="text-[11px] text-slate-500">AI is looking up specs and known failures for this model</div>
                </div>
              ) : specsData ? (
                <div data-testid="hvac-specs-result">
                  {specsFromCache && (
                    <div data-testid="hvac-specs-cache-badge" className="mb-3 text-[10px] uppercase font-black tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-block">
                      Loaded from 7-day cache · free lookup
                    </div>
                  )}
                  {specsData.identified === false && (
                    <div className="mb-4 p-3 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-amber-900 text-sm" data-testid="hvac-specs-unknown">
                      <div className="font-bold mb-0.5">Model not fully recognized</div>
                      <div className="text-xs">{specsData.note_if_uncertain || "Snap a clearer nameplate photo via ID Photo or add the brand for a tighter match. Any decoded fields below are best-guess."}</div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    {specsData.brand && <span className="text-[10px] font-black uppercase tracking-widest bg-slate-900 text-white px-2 py-1 rounded">{specsData.brand}</span>}
                    {specsData.family && <span className="text-sm font-bold text-slate-800">{specsData.family}</span>}
                    {specsData.confidence && (
                      <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
                        specsData.confidence==="high"?"bg-emerald-100 text-emerald-800":specsData.confidence==="low"?"bg-red-100 text-red-800":"bg-amber-100 text-amber-800"}`}>
                        {specsData.confidence} confidence
                      </span>
                    )}
                  </div>

                  {/* Nameplate decode */}
                  {specsData.nameplate_decode && Object.values(specsData.nameplate_decode).some(v => v) && (
                    <div className="mb-4" data-testid="hvac-specs-decode">
                      <div className="text-[10px] uppercase font-black tracking-widest text-cyan-700 mb-2">Nameplate Decode</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(specsData.nameplate_decode).filter(([, v]) => v).map(([k, v]) => (
                          <div key={k} className="p-2 border border-slate-200 rounded-lg">
                            <div className="text-[9px] uppercase font-black tracking-widest text-slate-400">{k.replace(/_/g, " ")}</div>
                            <div className="text-sm font-bold text-slate-800 mt-0.5">{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Field tests */}
                  {(specsData.field_tests || []).length > 0 && (
                    <div className="mb-4" data-testid="hvac-specs-tests">
                      <div className="text-[10px] uppercase font-black tracking-widest text-fuchsia-700 mb-2">Field Test Targets</div>
                      <div className="space-y-2">
                        {specsData.field_tests.map((t, i) => (
                          <div key={i} className="p-3 border border-slate-200 rounded-lg">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm">{t.test}</span>
                              {t.target && <span className="text-[11px] font-mono bg-fuchsia-100 text-fuchsia-800 px-2 py-0.5 rounded">{t.target}</span>}
                            </div>
                            {t.how && <div className="text-xs text-slate-600 mt-1">{t.how}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Common failures */}
                  {(specsData.common_failures || []).length > 0 && (
                    <div className="mb-4" data-testid="hvac-specs-failures">
                      <div className="text-[10px] uppercase font-black tracking-widest text-red-700 mb-2">Known Failure Points</div>
                      <div className="space-y-1.5">
                        {specsData.common_failures.map((f, i) => (
                          <div key={i} className="border-l-4 border-red-500 pl-3 py-1 flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">{f.part}</span>
                            <span className="text-xs text-slate-600">{f.why}</span>
                            {f.part && f.part !== "other" && (
                              <button type="button"
                                data-testid={`hvac-specs-reorder-${i}`}
                                onClick={async () => {
                                  try {
                                    await api.post("/parts-reorder", {
                                      part: f.part, qty: 1, source: "ai_specs",
                                      unit_ref: specsFor.equipment_id,
                                      job_ref: `${specsFor.nickname || specsFor.equipment_id} — ${specsFor.site_address || ""}`.trim(),
                                      notes: f.why,
                                    });
                                    toast.success(`Added ${f.part} → ${specsFor.nickname || specsFor.equipment_id}`);
                                  } catch { toast.error("Could not add to reorder"); }
                                }}
                                className="ml-auto h-6 px-2 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                                + Truck
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Install gotchas */}
                  {(specsData.install_gotchas || []).length > 0 && (
                    <div className="mb-3" data-testid="hvac-specs-gotchas">
                      <div className="text-[10px] uppercase font-black tracking-widest text-amber-700 mb-1">Install Gotchas</div>
                      <ul className="list-disc pl-5 text-sm space-y-1 text-amber-900">
                        {specsData.install_gotchas.map((g, i) => <li key={i}>{g}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="mt-4 text-[10px] text-slate-400 italic">
                    AI-generated reference — always verify against manufacturer install/service manual before making a call in the field.
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {loadCalcOpen && <LoadCalcPopover onClose={() => setLoadCalcOpen(false)}/>}
    </div>
    </AdminShell>
  );
}

const inputCls = "w-full h-11 px-3 border-2 border-slate-200 focus:border-[#00C4CC] outline-none rounded-lg font-medium";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
